import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import MotionReveal from "@/components/MotionReveal";
import { usePhotoLayouts, pickPhotos } from "@/hooks/usePhotoLayouts";
import SectionRule from "@/components/SectionRule";
import CountdownTimer from "@/components/CountdownTimer";
import WeatherWidget from "@/components/WeatherWidget";
import AndreasTip from "@/components/AndreasTip";
import AddToCalendarButton from "@/components/AddToCalendarButton";
import PersonalisedGreeting from "@/components/PersonalisedGreeting";
import AndreasRadio from "@/components/AndreasRadio";
import {
  HOME_HERO_DEFAULTS,
  HOME_STORY_PREVIEW_DEFAULTS,
  HOME_TRAVEL_PREVIEW_DEFAULTS,
} from "@/data/defaultPhotoLayouts";

const HERO_IMG = HOME_HERO_DEFAULTS[0];
const STORY_IMG = HOME_STORY_PREVIEW_DEFAULTS[0];
const TRAVEL_IMG = HOME_TRAVEL_PREVIEW_DEFAULTS[0];

const Home = () => {
  const { layouts } = usePhotoLayouts();
  const heroImg = pickPhotos(layouts, "home.hero", [HERO_IMG])[0] || HERO_IMG;
  const storyImg = pickPhotos(layouts, "home.storyPreview", [STORY_IMG])[0] || STORY_IMG;
  const travelImg = pickPhotos(layouts, "home.travelPreview", [TRAVEL_IMG])[0] || TRAVEL_IMG;
  const { lang, t, dict } = useLang();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 80]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.6]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="home-hero">
        <motion.div className="absolute inset-0" style={{ y: heroY, opacity: heroOpacity }}>
          <img
            src={heroImg}
            alt="Cypriot coast at golden hour"
            className="w-full h-full object-cover"
            loading="eager"
          />
          {/* Cinematic dark gradient — strongest where text lives (bottom half) */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(20,20,22,0.30) 0%, rgba(20,20,22,0.10) 30%, rgba(20,20,22,0.55) 65%, rgba(20,20,22,0.90) 100%)",
            }}
          />
        </motion.div>

        <div className="relative z-10 h-full mx-auto max-w-[1180px] px-5 sm:px-8 flex flex-col justify-end pb-20 sm:pb-28 text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.55)]">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="uppercase font-medium text-[13px] sm:text-[14px] tracking-[0.24em]"
            style={{ color: "#fff", textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.8)" }}
          >
            {t("home.heroOverline")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="editorial-h1 mt-5 text-[44px] sm:text-[72px] lg:text-[104px] leading-[0.98] max-w-[12ch] text-white"
          >
            {t("home.heroTitle")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-4 font-serif-display italic text-2xl sm:text-3xl text-white/95"
          >
            {t("home.heroSubtitle")}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-6 uppercase font-medium text-[12px] sm:text-[13px] tracking-[0.24em]"
            style={{ color: "#fff", textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.8)" }}
          >
            {t("home.heroLocation")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4 sm:gap-6"
          >
            <Link
              to={`/${lang}/rsvp`}
              data-testid="hero-rsvp-cta"
              className="inline-flex items-center h-12 px-6 rounded-xl bg-white text-foreground text-[12px] tracking-[0.22em] uppercase hover:bg-white/90 transition-colors [text-shadow:none]"
            >
              {t("home.rsvpCta")}
            </Link>
            <AddToCalendarButton variant="onDark" />
            <Link
              to={`/${lang}/story`}
              data-testid="hero-story-cta"
              className="text-[12px] tracking-[0.22em] uppercase border-b border-white/60 text-white hover:border-white hover:text-white/80 transition-colors"
            >
              {t("home.storyCta")}
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ duration: 1.4, delay: 1.5 }}
            className="uppercase font-medium text-[11px] tracking-[0.24em] mt-14 self-center sm:self-start"
            style={{ color: "#fff", textShadow: "0 1px 8px rgba(0,0,0,0.85)" }}
          >
            {t("home.scroll")}
          </motion.p>
        </div>
      </section>

      {/* PERSONALISED GREETING (only if guest came via magic link) */}
      <PersonalisedGreeting />

      {/* ANDREAS RADIO — a fresh Cyprus one-liner every visit */}
      <div className="pt-8 sm:pt-10">
        <AndreasRadio />
      </div>

      {/* COUNTDOWN */}
      <section
        className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-16 sm:pt-24 pb-4 sm:pb-8"
        data-testid="home-countdown"
      >
        <MotionReveal>
          <div className="grid gap-8 lg:grid-cols-12 items-end">
            <div className="lg:col-span-5">
              <p className="editorial-kicker">{t("home.countdownOverline")}</p>
              <h2 className="editorial-h2 mt-4 text-3xl sm:text-4xl lg:text-5xl max-w-[16ch]">
                {t("home.countdownTitle")}
              </h2>
            </div>
            <div className="lg:col-span-7">
              <CountdownTimer variant="light" showOverline={false} />
            </div>
          </div>
        </MotionReveal>
      </section>

      {/* WEATHER */}
      <section
        className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-8 sm:pt-12 pb-16 sm:pb-24"
        data-testid="home-weather"
      >
        <MotionReveal>
          <div className="grid gap-8 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <p className="editorial-kicker">{t("home.weatherOverline")}</p>
              <h2 className="editorial-h2 mt-4 text-3xl sm:text-4xl lg:text-5xl max-w-[16ch]">
                {t("home.weatherHistoricalTitle")}
              </h2>
            </div>
            <div className="lg:col-span-7">
              <WeatherWidget />
            </div>
          </div>
        </MotionReveal>
      </section>

      {/* WELCOME */}
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 py-20 sm:py-28">
        <MotionReveal>
          <SectionRule label={t("home.welcomeOverline")} />
        </MotionReveal>
        <MotionReveal delay={0.05} className="mt-10 grid gap-10 lg:grid-cols-12 items-end">
          <h2 className="editorial-h2 text-3xl sm:text-5xl lg:text-6xl lg:col-span-7 max-w-[18ch]">
            {t("home.welcomeTitle")}
          </h2>
          <p className="lg:col-span-5 text-foreground/75 leading-relaxed max-w-[44ch]">
            {t("home.welcomeBody")}
          </p>
        </MotionReveal>
      </section>

      {/* ANDREAS' TIP OF THE DAY */}
      <AndreasTip />

      {/* STORY PREVIEW */}
      <section className="bg-[hsl(34_28%_59%_/_0.08)] border-y border-border/60">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-8 py-20 sm:py-28 grid gap-12 lg:grid-cols-12 items-center">
          <MotionReveal className="lg:col-span-7">
            <img
              src={storyImg}
              alt="Eli and Mag"
              className="block w-full h-auto rounded-md shadow-[0_18px_50px_rgba(31,31,31,0.10)]"
              loading="lazy"
            />
          </MotionReveal>
          <MotionReveal delay={0.1} className="lg:col-span-5">
            <p className="editorial-kicker">{t("home.storyOverline")}</p>
            <h3 className="editorial-h2 mt-4 text-3xl sm:text-4xl max-w-[20ch]">
              {t("home.storyTitle")}
            </h3>
            <p className="mt-6 text-foreground/75 leading-relaxed max-w-[40ch]">
              {t("home.storyBody")}
            </p>
            <Link
              to={`/${lang}/story`}
              data-testid="home-story-link"
              className="inline-block mt-8 text-[12px] tracking-[0.22em] uppercase border-b border-foreground/40 hover:text-accent hover:border-accent transition-colors"
            >
              {t("home.storyCta")}
            </Link>
          </MotionReveal>
        </div>
      </section>

      {/* SCHEDULE PREVIEW */}
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 py-20 sm:py-28">
        <MotionReveal><SectionRule label={t("home.scheduleOverline")} /></MotionReveal>
        <MotionReveal delay={0.05}>
          <h3 className="editorial-h2 mt-10 text-3xl sm:text-5xl max-w-[18ch]">{t("home.scheduleTitle")}</h3>
        </MotionReveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {dict.schedule.items.map((it, i) => (
            <MotionReveal key={it.title} delay={0.06 * i}>
              <article className="surface-card p-6 h-full flex flex-col">
                <p className="editorial-kicker text-accent">{it.time}</p>
                <h4 className="font-serif-display text-2xl mt-3">{it.title}</h4>
                <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{it.place}</p>
              </article>
            </MotionReveal>
          ))}
        </div>
        <MotionReveal delay={0.2}>
          <Link
            to={`/${lang}/schedule`}
            data-testid="home-schedule-link"
            className="inline-block mt-10 text-[12px] tracking-[0.22em] uppercase border-b border-foreground/40 hover:text-accent hover:border-accent transition-colors"
          >
            {t("home.scheduleCta")}
          </Link>
        </MotionReveal>
      </section>

      {/* TRAVEL PREVIEW */}
      <section className="bg-[hsl(32_18%_92%)] border-y border-border/60">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-8 py-20 sm:py-28 grid gap-12 lg:grid-cols-12 items-center">
          <MotionReveal className="lg:col-span-5 order-2 lg:order-1">
            <p className="editorial-kicker">{t("home.travelOverline")}</p>
            <h3 className="editorial-h2 mt-4 text-3xl sm:text-4xl max-w-[20ch]">
              {t("home.travelTitle")}
            </h3>
            <p className="mt-6 text-foreground/75 leading-relaxed max-w-[40ch]">
              {t("home.travelBody")}
            </p>
            <Link
              to={`/${lang}/travel`}
              data-testid="home-travel-link"
              className="inline-block mt-8 text-[12px] tracking-[0.22em] uppercase border-b border-foreground/40 hover:text-accent hover:border-accent transition-colors"
            >
              {t("home.travelCta")}
            </Link>
          </MotionReveal>
          <MotionReveal delay={0.1} className="lg:col-span-7 order-1 lg:order-2">
            <img
              src={travelImg}
              alt="Quiet stair architecture"
              className="block w-full h-auto rounded-md shadow-[0_18px_50px_rgba(31,31,31,0.10)]"
              loading="lazy"
            />
          </MotionReveal>
        </div>
      </section>

      {/* RSVP CTA */}
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 py-24 sm:py-32 text-center">
        <MotionReveal>
          <p className="editorial-kicker">{t("home.rsvpOverline")}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h3 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl max-w-[18ch] mx-auto">
            {t("home.rsvpTitle")}
          </h3>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="editorial-kicker mt-6">{t("home.rsvpBody")}</p>
        </MotionReveal>
        <MotionReveal delay={0.15}>
          <Link
            to={`/${lang}/rsvp`}
            data-testid="home-rsvp-cta"
            className="inline-flex items-center h-12 px-7 mt-10 rounded-xl bg-foreground text-background text-[12px] tracking-[0.22em] uppercase hover:bg-foreground/90 transition-colors"
          >
            {t("home.rsvpCta")}
          </Link>
        </MotionReveal>
      </section>
    </div>
  );
};

export default Home;
